import React, { useState, useEffect, useRef } from "react";
import { Modal, Button, Table, Form } from "semantic-ui-react";
import "./CustomModal.css"; // Custom CSS file for modal styling
import styles from "./DataConnect.module.css";
import { generateToolContext } from "../../../../../../../api/DataConnection/openia";
// import api from '../../../../../../../api';

const AutoGenerated = ({ open, onClose, onConfirm, onApprove ,selectedtimezone,toolcontext=''}) => {
  const [showResponse, setShowResponse] = useState(false);
  const [apiKeys, setApiKeys] = useState([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isLoadingKeys, setIsLoadingKeys] = useState(false);
  const [apiError, setApiError] = useState(false);
  const [sampleQuestion, setSampleQuestion] = useState(
    `Each record includes:
start_time : define this
end_time : define this
is_available : define this
 
Sample record:
start_time 
end_time 
is_available
 
Sample user questions:
- "Any available slots on August 7th?"
- "Do we have any morning appointments for August 1st?"
- "What is the earliest available time on August 8?"
 
Output format:
 
If no match found:
"No available appointment matches your request."
 
 
Instructions
Only include slots where "Is this time available" is "TRUE".
        2. Include only slots where "Start time" is after the current time.
        3. If the user specifies a Business unit, return slots only for that unit.
        4. If the user specifies a date, return slots only for that date.
        5. If the user asks for ASAP, "soon", "next slot", or gives no time, return the earliest valid slot.
        6. If the user asks for a past date, respond: "Date is in the past."
        7. If no slots match, respond: "No slots available."
        8. Format: [Business unit]: YYYY-MM-DD HH:MM to HH:MM
        9. Limit response to under 10 words — no extra explanation.`
  );
  const [sampleOutput, setSampleOutput] = useState(
    "Please select the appropriate timezone from the dropdown below to ensure accurate scheduling."
  );
  const [generatedResponse, setGeneratedResponse] = useState("");
  const [responseHighlighted, setResponseHighlighted] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [tempResponseText, setTempResponseText] = useState("");
  const [responseGenerated, setResponseGenerated] = useState(false);
  const [isApproving, setIsApproving] = useState(false);
  const [approvalSuccess, setApprovalSuccess] = useState(false);
  const [apiProcessed, setApiProcessed] = useState(false);

  // Ref for response section
  const responseRef = useRef(null);
  const contentRef = useRef(null);

  const handleGenerateClick = async () => {
    setIsGenerating(true);
    setShowResponse(true);
    setResponseGenerated(true);

    try {
      // Call OpenAI API to generate context
      const result = await generateToolContext(
        sampleQuestion,
        sampleOutput,
        apiKeys,
        onConfirm,
        selectedtimezone
      );

      if (result.success) {
        const newResponse = result.context;
        setGeneratedResponse(newResponse);
        setTempResponseText(newResponse);
        setResponseGenerated(true);
        console.log("OpenAI API call successful:", result);
        console.log("Usage:", result.usage);
      } else {
        // Fallback to default response if API fails
        const fallbackResponse =
          "API call failed. Based on the available keys and your sample question, here is a generated relevant context for your tool. This context will help users understand what information is expected and how to properly use this tool in their conversations.";
        setGeneratedResponse(fallbackResponse);
        setTempResponseText(fallbackResponse);
        setResponseGenerated(true);

        console.error("OpenAI API call failed:", result.error);
      }
    } catch (error) {
      console.error("Error calling OpenAI API:", error);

      // Fallback response
      const fallbackResponse =
        "Error occurred while generating context. Based on the available keys and your sample question, here is a generated relevant context for your tool. This context will help users understand what information is expected and how to properly use this tool in their conversations.";
      setGeneratedResponse(fallbackResponse);
      setTempResponseText(fallbackResponse);
      setResponseGenerated(true);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleReGenerate = async () => {
    setIsGenerating(true);

    try {
      // Call OpenAI API to re-generate context
      const result = await generateToolContext(
        sampleQuestion,
        sampleOutput,
        apiKeys,
        onConfirm,
        selectedtimezone
      );

      if (result.success) {
        const newResponse = result.context;
        setGeneratedResponse(newResponse);
        setTempResponseText(newResponse);
      } else {
        // Fallback to default response if API fails
        const fallbackResponse =
          "Re-generation failed. Based on the updated analysis of your sample inputs and available keys, here is an improved context for your tool. This enhanced context provides better guidance for users.";
        setGeneratedResponse(fallbackResponse);
        setTempResponseText(fallbackResponse);
      }
    } catch (error) {
      const fallbackResponse =
        "Error occurred during re-generation. Based on the updated analysis of your sample inputs and available keys, here is an improved context for your tool. This enhanced context provides better guidance for users.";
      setGeneratedResponse(fallbackResponse);
      setTempResponseText(fallbackResponse);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleEdit = () => {
    setIsEditMode(true);
    setTempResponseText(generatedResponse);

    // Focus the textarea after state update
    setTimeout(() => {
      const responseTextarea = responseRef.current?.querySelector("textarea");
      if (responseTextarea) {
        responseTextarea.focus();
        responseTextarea.setSelectionRange(
          responseTextarea.value.length,
          responseTextarea.value.length
        );
      }
    }, 100);
  };

  const handleSave = () => {
    console.log("Saving edited response:", tempResponseText);
    setGeneratedResponse(tempResponseText);
    setIsEditMode(false);
  };

  const handleCancelEdit = () => {
    console.log("Cancelling edit mode");
    setTempResponseText(generatedResponse);
    setIsEditMode(false);
  };

  const handleApprove = () => {
    console.log("Approved response:", generatedResponse);
    setIsApproving(true);

    // Simulate approval process
    setTimeout(() => {
      setApprovalSuccess(true);
      setIsApproving(false);

      // Call the onApprove callback to update parent component
      if (onApprove && typeof onApprove === "function") {
        onApprove(generatedResponse);
      }

      // Show success message briefly then close
      setTimeout(() => {
        setApprovalSuccess(false);
        onClose();
      }, 1500);
    }, 500);
  };

  const handleTempResponseChange = (e) => {
    setTempResponseText(e.target.value);
  };

  const handleSampleQuestionChange = (e) => {
    setSampleQuestion(e.target.value);
  };

  const handleSampleOutputChange = (e) => {
    setSampleOutput(e.target.value);
  };

  // Extract keys from API response
  useEffect(() => {
    // Use setTimeout to ensure state updates happen properly
    setTimeout(() => {
      if (onConfirm) {
        setIsLoadingKeys(true);
        setApiError(false);
        let keys = [];

        try {
          // Check if onConfirm contains error information
          if (
            onConfirm.error ||
            onConfirm.message === "Internal Server Error"
          ) {
            setApiError(true);
            setApiKeys([]);
            setIsLoadingKeys(false);
            return;
          }

          if (Array.isArray(onConfirm) && onConfirm.length > 0) {
            keys = Object.keys(onConfirm[0]);
          } else if (
            onConfirm.data &&
            Array.isArray(onConfirm.data) &&
            onConfirm.data.length > 0
          ) {
            keys = Object.keys(onConfirm.data[0]);
          } else if (
            onConfirm.data &&
            typeof onConfirm.data === "object" &&
            !Array.isArray(onConfirm.data)
          ) {
            keys = Object.keys(onConfirm.data);
          } else if (
            typeof onConfirm === "object" &&
            onConfirm !== null &&
            !Array.isArray(onConfirm)
          ) {
            keys = Object.keys(onConfirm);
          }

          // Filter out empty or invalid keys and specific keys to exclude
          const keysToExclude = ["row_key", "sync_time", "client_id", "db_id"];
          keys = keys.filter(
            (key) => key && key.trim() !== "" && !keysToExclude.includes(key)
          );

          setApiKeys(keys);
          setIsLoadingKeys(false);
          setApiProcessed(true);
        } catch (error) {
          console.error("Error processing API response:", error);
          setApiError(true);
          setApiKeys([]);
          setIsLoadingKeys(false);
          setApiProcessed(true);
        }
      } else if (open) {
        setApiKeys([]);
        setIsLoadingKeys(false);
        setApiError(false);
        setApiProcessed(false);
      }
    }, 100); // Small delay to ensure proper state updates
  }, [onConfirm, open]);

  // Set initial loading state when modal opens
  useEffect(() => {
    if (open) {
      // Reset states when modal opens
      setApiKeys([]);
      setApiError(false);
      setApiProcessed(false);
      setShowResponse(false);
      setResponseGenerated(false);
      setGeneratedResponse("");
      setTempResponseText("");
      setIsEditMode(false);
      setIsApproving(false);
      setApprovalSuccess(false);

      if (!onConfirm) {
        setIsLoadingKeys(true);
      }
    }
  }, [open]);

  // Auto-scroll to response section when it becomes visible
  useEffect(() => {
    if (showResponse && responseRef.current) {
      // Add a delay to ensure DOM is fully rendered
      setTimeout(() => {
        setResponseHighlighted(true);

        // Try multiple scroll methods for better compatibility
        try {
          // Method 1: Direct scrollIntoView
          responseRef.current.scrollIntoView({
            behavior: "smooth",
            block: "center",
            inline: "nearest",
          });

          // Method 2: Fallback for modal content scroll
          const modalContent = document.querySelector(".auto-generate-content");
          if (modalContent) {
            const elementTop = responseRef.current.offsetTop;
            const modalTop = modalContent.scrollTop;
            const modalHeight = modalContent.clientHeight;
            const targetPosition = elementTop - modalHeight / 3;

            modalContent.scrollTo({
              top: Math.max(0, targetPosition),
              behavior: "smooth",
            });
          }
        } catch (error) {
          console.log("Scroll error:", error);
        }

        // Focus on textarea after scroll
        setTimeout(() => {
          const responseTextarea =
            responseRef.current.querySelector("textarea");
          if (responseTextarea && !isEditMode) {
            try {
              responseTextarea.focus();
              responseTextarea.scrollIntoView({
                behavior: "smooth",
                block: "nearest",
              });
            } catch (error) {
              console.log("Focus error:", error);
            }
          }

          // Remove highlight after animation
          setTimeout(() => {
            setResponseHighlighted(false);
          }, 1500);
        }, 800);
      }, 300);
    }
  }, [showResponse]);

  // Update sample question when apiKeys change
  useEffect(() => {
    if (apiKeys && apiKeys.length > 0 && onConfirm) {
      const keyDefinitions = apiKeys.map(key => `${key} : define this`).join('\n');
      
      // Get actual values from API response
      let sampleData = null;
      try {
        if (Array.isArray(onConfirm) && onConfirm.length > 0) {
          sampleData = onConfirm[0];
        } else if (onConfirm.data && Array.isArray(onConfirm.data) && onConfirm.data.length > 0) {
          sampleData = onConfirm.data[0];
        } else if (onConfirm.data && typeof onConfirm.data === "object" && !Array.isArray(onConfirm.data)) {
          sampleData = onConfirm.data;
        } else if (typeof onConfirm === "object" && onConfirm !== null && !Array.isArray(onConfirm)) {
          sampleData = onConfirm;
        }
      } catch (error) {
        console.error("Error extracting sample data:", error);
      }

      // Create JSON object for sample record
      const sampleRecord = {};
      apiKeys.forEach(key => {
        const value = sampleData && sampleData[key] !== undefined ? sampleData[key] : 'sample_value';
        sampleRecord[key] = value;
      });
      
      const sampleRecords = JSON.stringify([sampleRecord], null, 2);
      
      setSampleQuestion(`Each record includes:
${keyDefinitions}
 
Sample record:
${sampleRecords}
 
Sample user questions:
- "Any available slots on August 7th?"
- "Do we have any morning appointments for August 1st?"
- "What is the earliest available time on August 8?"
 
Output format:
 
If no match found:
"No available appointment matches your request."
 
 
Instructions
Only include slots where "Is this time available" is "TRUE".
        2. Include only slots where "Start time" is after the current time.
        3. If the user specifies a Business unit, return slots only for that unit.
        4. If the user specifies a date, return slots only for that date.
        5. If the user asks for ASAP, "soon", "next slot", or gives no time, return the earliest valid slot.
        6. If the user asks for a past date, respond: "Date is in the past."
        7. If no slots match, respond: "No slots available."
        8. Format: [Business unit]: YYYY-MM-DD HH:MM to HH:MM
                 9. Limit response to under 10 words — no extra explanation.`);
    }
  }, [apiKeys, onConfirm]);

  // Reset state when modal opens or closes
  useEffect(() => {
    if (!open) {
      // Clear all states when modal closes
      setShowResponse(false);
      setApiKeys([]);
      setIsGenerating(false);
      setIsLoadingKeys(false);
      setApiError(false);
      setApiProcessed(false);
      setResponseHighlighted(false);
      setIsEditMode(false);
      setTempResponseText("");
      setResponseGenerated(false);
      setIsApproving(false);
      setApprovalSuccess(false);
      setSampleQuestion(`Each record includes:
start_time : define this
end_time : define this
is_available : define this
 
Sample record:
start_time 
end_time 
is_available
 
Sample user questions:
- "Any available slots on August 7th?"
- "Do we have any morning appointments for August 1st?"
- "What is the earliest available time on August 8?"
 
Output format:
 
If no match found:
"No available appointment matches your request."
 
 
Instructions
Only include slots where "Is this time available" is "TRUE".
        2. Include only slots where "Start time" is after the current time.
        3. If the user specifies a Business unit, return slots only for that unit.
        4. If the user specifies a date, return slots only for that date.
        5. If the user asks for ASAP, "soon", "next slot", or gives no time, return the earliest valid slot.
        6. If the user asks for a past date, respond: "Date is in the past."
        7. If no slots match, respond: "No slots available."
        8. Format: [Business unit]: YYYY-MM-DD HH:MM to HH:MM
        9. Limit response to under 10 words — no extra explanation.`);
      setSampleOutput(`old context: ${toolcontext}. Create new context with the following instructions from the old context.Do not remove dynamic variables({{CURRENTTIME}},{{CURRENTDATE}},{{CURRENTDATETIME}}) from the old context if any used in old context. Use variables in the new context for time and date.Instructions are below:

       - You are a smart scheduling assistant.Your job is to reply with available time slots from the provided schedule.

        CURRENT TIME
         - Current date and time: ${new Date().toISOString()}
      
        
        DATA FIELDS
        - "Business unit": Type of service (e.g., HVAC, Electrical - Maintenance)
        - "Start time": ISO string (e.g., 2025-06-09T10:00:00Z)
        - "End time": ISO string (e.g., 2025-06-09T14:00:00Z)
        - "Is this time available": Must be "TRUE" to qualify
        - Ignore: "row_key", "sync_time", "client_id", "db_id"
        
        INSTRUCTIONS
        1. Only include slots where "Is this time available" is "TRUE".
        2. Include only slots where "Start time" is after the current time.
        3. If the user specifies a Business unit, return slots only for that unit.
        4. If the user specifies a date, return slots only for that date.
        5. If the user asks for ASAP, "soon", "next slot", or gives no time, return the earliest valid slot.
        6. If the user asks for a past date, respond: "Date is in the past."
        7. If no slots match, respond: "No slots available."
        8. Format: [Business unit]: YYYY-MM-DD HH:MM to HH:MM
        9. Limit response to under 10 words — no extra explanation.`);
      setGeneratedResponse("");
    }
  }, [open]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      className="custom-modal-layer"
      style={{
        display: "flex",
        flexDirection: "column",
        maxHeight: "90vh",
        overflow: "visible",
      }}
    >
      <Modal.Header className="auto-generate-header">
        <div className="header-content">
          <span className="header-title"> Auto Generate Relevant Context</span>
          <button
            type="button"
            className="linear-gradient close-btn"
            onClick={onClose}
          >
            <i className="la la-times"></i>
          </button>
        </div>
      </Modal.Header>
      <Modal.Content
        className="auto-generate-content"
        ref={contentRef}
        style={{
          flex: 1,
          overflowY: "auto",
          maxHeight: "calc(90vh - 180px)",
          marginBottom: 0,
        }}
      >
        <div className="modal-body-custom">
          <div className="keys-section">
            <div className="keys-label">Available Keys:</div>
            <div className="keys-container">
              {isLoadingKeys || (!apiProcessed && open) ? (
                <div className="keys-loading">
                  <span className="key-badge loading-badge">Loading...</span>
                </div>
              ) : apiKeys && apiKeys.length > 0 ? (
                apiKeys.map((key, index) => (
                  <span key={index} className="key-badge">
                    {key}
                  </span>
                ))
              ) : (
                <span className="key-badge">No keys available</span>
              )}
            </div>
          </div>

          <div className="content-section">
            <div className="form-row">
              <div className="form-column">
                <div className="form-group">
                  <label className="form-label">Sample Question</label>
                  <textarea
                    className="form-textarea"
                    placeholder="In which timezone will this event or call occur?"
                    rows="4"
                    value={sampleQuestion}
                    onChange={handleSampleQuestionChange}
                    style={{ minHeight: "150px", height:"307px" }}
                  />
                </div>
              </div>
              <div className="form-column">
                <div className="form-group">
                  <label className="form-label">Sample Output</label>
                  <textarea
                    className="form-textarea"
                    placeholder="Please select the appropriate timezone from the dropdown below to ensure accurate scheduling."
                    rows="4"
                    value={sampleOutput}
                    onChange={handleSampleOutputChange}
                    style={{ minHeight: "150px", height:"307px" }}
                  />
                </div>
              </div>
            </div>

            {showResponse && (
              <div
                className={`response-section ${
                  responseHighlighted ? "focused" : ""
                } ${approvalSuccess ? "approved" : ""}`}
                ref={responseRef}
              >
                {/* Success Message */}
                {approvalSuccess && (
                  <div className="approval-success-message">
                    <i className="la la-check-circle"></i>
                    <span>
                      Context approved and applied to Tool Context field!
                    </span>
                  </div>
                )}

                <div className="form-group">
                  <label className="form-label">Generated Response</label>
                  <textarea
                    className="form-textarea response-textarea"
                    placeholder="Generated context will appear here..."
                    rows="4"
                    value={isEditMode ? tempResponseText : generatedResponse}
                    onChange={isEditMode ? handleTempResponseChange : undefined}
                    readOnly={!isEditMode}
                  />
                </div>

                {/* Response Action Buttons */}
                {responseGenerated && !isGenerating && (
                  <div className="response-actions">
                    {isEditMode ? (
                      // Edit Mode Buttons
                      <div className="edit-mode-buttons">
                        <button
                          type="button"
                          className="btn-save linear-gradient"
                          onClick={handleSave}
                        >
                          Save
                        </button>
                        <button
                          type="button"
                          className="btn-cancel-edit"
                          onClick={handleCancelEdit}
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      // View Mode Buttons
                      <div className="view-mode-buttons">
                        <button
                          type="button"
                          className="btn-edit linear-gradient"
                          onClick={handleEdit}
                        >
                          <i className="la la-edit"></i> Edit
                        </button>
                        <button
                          type="button"
                          className="btn-regenerate linear-gradient"
                          onClick={handleReGenerate}
                          disabled={isGenerating}
                        >
                          <i className="la la-refresh"></i>{" "}
                          {isGenerating ? "Calling OpenAI..." : "Re-Generate"}
                        </button>
                        <button
                          type="button"
                          className={`linear-gradient btn-approve ${
                            isApproving ? "loading" : ""
                          } ${approvalSuccess ? "success" : ""}`}
                          onClick={handleApprove}
                          disabled={isApproving || approvalSuccess}
                        >
                          {approvalSuccess ? (
                            <>
                              <i className="la la-check-circle"></i> Approved &
                              Applied!
                            </>
                          ) : isApproving ? (
                            <>
                              <i className="la la-spinner la-spin"></i>{" "}
                              Approving...
                            </>
                          ) : (
                            <>
                              <i className="la la-check"></i> Approve
                            </>
                          )}
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {/* Loading message during generation */}
                {isGenerating && (
                  <div className="generation-loading">
                    <div className="loading-spinner">
                      <i className="la la-spinner la-spin"></i>
                      <span>Calling OpenAI API to generate context...</span>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </Modal.Content>
      <div
        className="auto-generate-actions"
        style={{
          display: "flex",
          justifyContent: "flex-end",
          gap: "1rem",
          background: "white",
          borderTop: "1px solid #e9ecef",
          borderRadius: "0 0 12px 12px",
          position: "relative",
          zIndex: 1000,
          width: "100%",
          boxSizing: "border-box",
        }}
      >
        <button
          type="button"
          className="btn-cancel"
          onClick={onClose}
          style={{
            display: "inline-block",
            visibility: "visible",
            opacity: 1,
            padding: "0.75rem 1.5rem",
            border: "2px solid #6c757d",
            background: "white",
            color: "#6c757d",
            borderRadius: "6px",
            fontSize: "0.9rem",
            fontWeight: 500,
            cursor: "pointer",
            minWidth: "100px",
            textAlign: "center",
          }}
        >
          {responseGenerated ? "Close" : "Cancel"}
        </button>

        {!responseGenerated ? (
          <button
            type="button"
            className={`btn-generate ${
              isGenerating ? "loading" : ""
            } linear-gradient yr-submitbtn`}
            onClick={handleGenerateClick}
            disabled={isGenerating}
            style={{
              display: "inline-block",
              visibility: "visible",
              opacity: 1,
              padding: "0.75rem 1.5rem",
              color: "white",
              border: "none",
              borderRadius: "6px",
              fontSize: "0.9rem",
              fontWeight: 500,
              cursor: "pointer",
              minWidth: "140px",
              textAlign: "center",
            }}
          >
            {isGenerating ? "Calling OpenAI API..." : "Generate Context"}
          </button>
        ) : null}
      </div>
    </Modal>
  );
};

export default AutoGenerated;
